import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Avatar, Loader, Icon } from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { ServiceManager } from '@jutro/legacy/services';
import { PhoneUtil } from '@xengage/gw-portals-viewmodel-js';
import { withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { useHistory, withRouter } from 'react-router-dom';
import { ViewModelForm, ViewModelServiceContext, withViewModelService } from '@xengage/gw-portals-viewmodel-react';
import cx from 'classnames';
import { ContactService } from 'gw-capability-gateway';
import _ from 'lodash';
import PropTypes from 'prop-types';
import gatewayMessages from '../../gateway.messages';
import messages from '../Contacts.messages';
import tabStyles from '../Contacts.module.scss';
import metadata from './AccountContactsPage.metadata.json5';

function AccountContactsPage(props) {
    const { accountNumber, relatedTo, authHeader } = props;
    const history = useHistory();
    const viewModelService = useContext(ViewModelServiceContext);
    const [accountContactRoleVM, setAccountContactRoleVM] = useState({});
    const [accountContactRole, setAccountContactRole] = useState('allRoles');
    const [accountContactRoleOptions, setAccountContactRoleOptions] = useState([]);
    const [accountContactTableData, setAccountContactTableData] = useState({});
    const [ contactData , setContactData ] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const TWIA_EXCLUDE_ROLES = ['APDInvolvedParty', 'AccountContactRole']
    const TFPA_EXCLUDES_ROLES = ['APDInvolvedParty', 'AccountContactRole','AccountingContact','Driver',
        'LaborClient','LaborContractor','OwnerOfficer','SecondaryContact']
    const translator = useContext(TranslatorContext);
    const PRIMARY_PHONE_TYPE_MAP = {
        home: 'homeNumber',
        mobile: 'cellNumber',
        work: 'workNumber',
    };

    useEffect(() => {
        createACRVM();
        const options = getContactRoles();
        setAccountContactRoleOptions(options);
        getAccountContacts();
    }, []);

    const createACRVM = useCallback(() =>{
        const contactRole = 'edge.capabilities.gateway.contact.dto.roles.account.AccountContactRoleDTO';
        const contactRoleVM = viewModelService.create({}, 'pc', contactRole);
        
        setAccountContactRoleVM(contactRoleVM);
    },[viewModelService]);

    const writeValue = value => {
        setAccountContactRole(value);
    };

    const getAccountContacts = useCallback(async (selectedOption) => {
        try {
            setIsLoading(true);
            setAccountContactRole(selectedOption);
            const role = selectedOption !== 'allRoles' ? [selectedOption] : [];
            const response = await ContactService.getAccountContacts(accountNumber, authHeader, role, 0, 25);
            setAccountContactTableData(response);

            const data = getResponseData(response.accountContacts);
            setContactData(data);

            return {
                rows: data,
                numberOfRows: response.maxNumberOfResults,
            };
        } catch (error) {
            console.error('Error fetching account contacts:', error);
        } finally {
            setIsLoading(false);
        }
    }, [accountNumber, authHeader, accountContactTableData.maxNumberOfResults]);

    const getContactRoles = () => {
        const allRolesOption = {
            code: 'allRoles',
            name: translator(messages.allRoles),
        };
        const rolesTypeList = viewModelService.productMetadata.get('pc').types.getTypelist('AccountContactRole');
        const EXCLUDES_ROLES = props.authUserData.associationName_TWIA === 'twia' ? TWIA_EXCLUDE_ROLES : 
                                TFPA_EXCLUDES_ROLES // DE16743
        const roleOptions = rolesTypeList.codes
            .filter(role => !_.includes(EXCLUDES_ROLES, role.code))
            .map(role => ({
                code: role.code,
                name: translator({
                    id: `gateway.contacts.common.views.roles.${role.code}`,
                    defaultMessage: `${role.code}`,
                }),
            }));

        roleOptions.unshift(allRolesOption);

        return roleOptions;
    };

    const getResponseData = accountContacts => {
        if (accountContacts) {
            return accountContacts.map(contact => ({
                contact: contact.displayName,
                role: contact.accountContactRoles,
                involvedWith: contact.associatedPoliciesCount,
                active: contact.active,
            }));
        }
        return [];
    };

    const redirectToContactDetails = index => {
        const { accountContacts } = accountContactTableData;
        history.push({
            pathname: `/${relatedTo}/${accountNumber}/contactDetails`,
            state: {
                contact: 'accountContacts',
                accountOrPolicyNumber: accountNumber,
                relatedTo: relatedTo,
                contactId: accountContacts[index].accountContactPublicID,
            },
        });
    };

    const getPhoneNumber = (primaryPhoneNumber) => (
        primaryPhoneNumber && 
            <div><Icon
                    tag="span"
                    icon="mi-phone"
                    className={tabStyles.icons}
                />{primaryPhoneNumber}
            </div>
    );
    
    const getEmail = (contact) => (
        contact.emailAddress1 && <div><Icon
                tag="span"
                icon="mi-mail"
                className={tabStyles.icons}
                />{contact.emailAddress1}
        </div>
    );

    const renderContact = (item, index, { path: property }) => {
        if (index < accountContactTableData.accountContacts.length) {
            const contact = accountContactTableData.accountContacts[index];

            const phoneNumber =
                contact[PRIMARY_PHONE_TYPE_MAP[contact?.primaryPhoneType]];
            const localeService =
                ServiceManager.getService('locale-service');
            const phoneUtil = PhoneUtil();
            const primaryPhoneNumber = phoneUtil.prettyPrint(
                phoneNumber,
                localeService.getDefaultCountryCode()
            );

            const contactAvatarClass = cx(tabStyles.gwContactAvatar, {
                [tabStyles.gwActiveContactAvatar]: item.active,
                [tabStyles.gwInactiveContactAvatar]: !item.active,
            });

            return (
                <div className={tabStyles.gwContactsTableWrapper}>
                    <div className={tabStyles.gwContactAvatarContainer}>
                        <Avatar
                            className={contactAvatarClass}
                            username={item[property]}
                            onClick={() => redirectToContactDetails(index)}
                        />
                    </div>
                    <div className={tabStyles.gwContactNameContainer}>
                        <div className={tabStyles.gwContactName}>
                            {item[property]}
                        </div>
                        {getPhoneNumber(primaryPhoneNumber)}
                        {getEmail(contact)}
                    </div>
                </div>
            );
        }

        return <div />;
    };

    const renderRole = (item, index, { path: property }) => {
        const roles = item[property];
        const roleOptions = accountContactRoleVM.subtype.aspects.availableValues;
        const getAllRoles = roles
            .map(roleCode => {
                const matchingRole = roleOptions.find(opt => opt.code === roleCode);
                return matchingRole
                    ? translator({
                          id: `gateway.contacts.common.views.roles.${matchingRole.code}`,
                          defaultMessage: `${matchingRole.code}`,
                      })
                    : null;
            })
            .join(', ');

        return <div title={getAllRoles}>{getAllRoles}</div>;
    };

    const renderPoliciesInvolved = (item, index, { path: property }) => {
        const policiesCount = item[property];
        const isActive = item.active;

        const policiesInvolved = isActive ? (
            <div className={tabStyles.gwInvolvedPoliciesContainer}>
                <h4 className={tabStyles.gwInvolvedPolicies}>
                    {policiesCount}
                </h4>
                {policiesCount > 1
                    ? translator(messages.activePolicies)
                    : translator(messages.activePolicy)}
            </div>
        ) : (
            translator(gatewayMessages.inactive)
        );

        return <div>{policiesInvolved}</div>;
    };

    const overrideProps = {
        accountContactsDropdown: {
            availableValues: accountContactRoleOptions,
            value: accountContactRole,
            defaultValue: 'allRoles'
        },
        accountContactsDataTable: {
            key: accountContactRole,
            data: contactData
        }
    };

    const resolvers = {
        resolveClassNameMap: tabStyles,
        resolveCallbackMap: {
            renderContact,
            renderRole,
            renderPoliciesInvolved,
            onRowClick: (data, index) => redirectToContactDetails(index),
            handleAccountContactSelect: getAccountContacts,
        },
    };

    const readValue = (id, path) => {
        return readViewModelValue(
            metadata.pageContent,
            accountContactRole,
            id,
            path,
            overrideProps
        );
    };

    return (
        <Loader loaded={!isLoading}>
            <ViewModelForm
                uiProps={metadata.pageContent}
                onValueChange={writeValue}
                resolveValue={readValue}
                overrideProps={overrideProps}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
            />
        </Loader>
    );
};

AccountContactsPage.propTypes = {
    viewModelService: PropTypes.shape({
        create: PropTypes.func,
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func,
    }).isRequired,
    accountNumber: PropTypes.string.isRequired,
    relatedTo: PropTypes.string.isRequired,
    authHeader: PropTypes.shape({}).isRequired,
};

export default withRouter(
    withViewModelService(withAuthenticationContext(AccountContactsPage))
);
